import { useState, useEffect, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, ChevronRight, Download, Share2, ArrowLeft } from "lucide-react";
import { loadIssuePhoto, isNativePlatform } from "@/lib/issue-photo";

export interface IssuePhoto {
  _id: Id<"issuePhotos">;
  path: string;
  note?: string;
  createdAt: number;
}

interface ImageViewerProps {
  photos: IssuePhoto[];
  currentIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIndexChange?: (index: number) => void;
  onSave?: (imageDataUrl: string) => void;
  onShare?: (imageDataUrl: string) => void;
  onNoteUpdated?: () => void;
}

export function ImageViewer({
  photos,
  currentIndex,
  open,
  onOpenChange,
  onIndexChange,
  onSave,
  onShare,
  onNoteUpdated,
}: ImageViewerProps) {
  const [loadedImages, setLoadedImages] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editNote, setEditNote] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);
  const updateNote = useMutation(api.issuePhotos.updateNote);

  const currentPhoto = photos[currentIndex];

  // Reset editing state when photo changes or viewer closes
  useEffect(() => {
    setIsEditing(false);
    setEditNote("");
  }, [currentIndex, open]);

  // Load current and adjacent images for smooth navigation
  useEffect(() => {
    if (!open || photos.length === 0) return;

    const loadImages = async () => {
      setIsLoading(true);
      const indicesToLoad = [
        currentIndex,
        (currentIndex + 1) % photos.length,
        (currentIndex - 1 + photos.length) % photos.length,
      ];

      for (const idx of indicesToLoad) {
        const path = photos[idx]?.path;
        if (path && !loadedImages[path] && isNativePlatform()) {
          const dataUrl = await loadIssuePhoto(path);
          if (dataUrl) {
            setLoadedImages((prev) => ({ ...prev, [path]: dataUrl }));
          }
        }
      }
      setIsLoading(false);
    };

    loadImages();
  }, [open, currentIndex, photos, loadedImages]);

  const handleEditNote = () => {
    setEditNote(currentPhoto?.note || "");
    setIsEditing(true);
  };

  const handleSaveNote = async () => {
    if (!currentPhoto) return;
    setIsSavingNote(true);
    try {
      await updateNote({
        photoId: currentPhoto._id,
        note: editNote.trim() || undefined,
      });
      onNoteUpdated?.();
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update note:", error);
    } finally {
      setIsSavingNote(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle navigation when editing note
      if (isEditing) return;

      if (e.key === "ArrowLeft") {
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        goToNext();
      } else if (e.key === "Escape") {
        onOpenChange(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, currentIndex, photos.length, isEditing]);

  // Swipe gesture handling
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && photos.length > 1) {
      goToNext();
    } else if (isRightSwipe && photos.length > 1) {
      goToPrevious();
    }
  };

  const goToPrevious = useCallback(() => {
    const newIndex = (currentIndex - 1 + photos.length) % photos.length;
    onIndexChange?.(newIndex);
  }, [currentIndex, photos.length, onIndexChange]);

  const goToNext = useCallback(() => {
    const newIndex = (currentIndex + 1) % photos.length;
    onIndexChange?.(newIndex);
  }, [currentIndex, photos.length, onIndexChange]);

  const currentImageUrl = currentPhoto ? loadedImages[currentPhoto.path] : undefined;

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay className="bg-black/90" />
      <DialogContent
        className="border-0 p-0 w-screen h-screen max-w-none m-0 bg-black/90 flex items-center justify-center"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close Button */}


        {/* Previous Button */}
        {photos.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
            onClick={goToPrevious}
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>
        )}

        {/* Next Button */}
        {photos.length > 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
            onClick={goToNext}
          >
            <ChevronRight className="h-8 w-8" />
          </Button>
        )}

        {/* Image Counter */}
        {photos.length > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
            {currentIndex + 1} / {photos.length}
          </div>
        )}

        {/* Image */}
        <div className="absolute inset-0 flex items-center justify-center w-full h-full">
          {isLoading || !currentImageUrl ? (
            <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <img
              src={currentImageUrl}
              alt={`Issue photo ${currentIndex + 1}`}
              className="w-full h-full object-contain"
              draggable={false}
            />
          )}
        </div>

        {/* Note Display/Edit */}
        {currentPhoto && (
          <div className="absolute bottom-20 left-4 right-4 z-10">
            {isEditing ? (
              <div className="bg-black/80 rounded-lg p-3 space-y-2">
                <Textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  placeholder="Add a note..."
                  rows={3}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white"
                    onClick={() => setIsEditing(false)}
                    disabled={isSavingNote}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveNote}
                    disabled={isSavingNote}
                  >
                    {isSavingNote ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            ) : currentPhoto.note ? (
              <div
                className="bg-black/70 rounded-lg p-3 cursor-pointer"
                onClick={handleEditNote}
              >
                <p className="text-white text-sm">{currentPhoto.note}</p>
                <p className="text-white/50 text-xs mt-1">Tap to edit</p>
              </div>
            ) : (
              <Button
                variant="ghost"
                className="text-white/70 text-sm"
                onClick={handleEditNote}
              >
                + Add note
              </Button>
            )}
          </div>
        )}

        {/* Bottom Action Bar */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-3">
          <Button
            variant="secondary"
            className="bg-black/50 text-white hover:bg-black/70 border-white/20"
            onClick={() => onOpenChange(false)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span className="text-sm">Back</span>
          </Button>
          <Button
            variant="secondary"
            className="bg-black/50 text-white hover:bg-black/70 border-white/20"
            onClick={() => {
              if (onSave && currentImageUrl) {
                onSave(currentImageUrl);
              }
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Save
          </Button>

          <Button
            variant="secondary"
            className="bg-black/50 text-white hover:bg-black/70 border-white/20"
            onClick={() => {
              if (onShare && currentImageUrl) {
                onShare(currentImageUrl);
              }
            }}
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
